import Agilite from 'agilite'
import store from '../../../store'
import ScriptEnums from './enums'
import CoreEnums from '../../../core/utils/enums'
import axios from 'axios'
import { createLookup } from '../../lib/utils'

const agilite = new Agilite({
  apiServerUrl: process.env.REACT_APP_AGILITE_API_URL,
  apiKey: process.env.REACT_APP_AGILITE_API_KEY
})

export const countTotalScripts = (qry, patientQry) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const pipeline = []

        // 1. If there's a general query for scripts, apply it first
        if (qry) {
          pipeline.push({ $match: qry })
        }

        // 2. If there's a patient query, add the patient lookup and filtering
        if (patientQry) {
          // Lookup patients using userRef directly (no ObjectId conversion)
          pipeline.push({
            $lookup: {
              from: `${process.env.REACT_APP_AGILITE_TEAM_ID}_auth_users`,
              localField: 'userRef',
              foreignField: '_id',
              as: 'patient',
              pipeline: [
                {
                  $match: {
                    $or: [
                      { firstName: { $regex: patientQry, $options: 'i' } },
                      { lastName: { $regex: patientQry, $options: 'i' } },
                      { phoneNumber: { $regex: patientQry, $options: 'i' } },
                      { email: { $regex: patientQry, $options: 'i' } },
                      { physicalAddress: { $regex: patientQry, $options: 'i' } },
                      { idNo: { $regex: patientQry, $options: 'i' } }
                    ]
                  }
                }
              ]
            }
          })

          // Only keep scripts where we found matching patients
          pipeline.push({
            $match: {
              patient: { $ne: [] }
            }
          })
        }

        // 3. Count the results
        pipeline.push({ $count: 'total' })

        // Execute the aggregation
        const response = await agilite.Connectors.execute(
          ScriptEnums.profileKeys.SCRIPTS,
          ScriptEnums.routeKeys.AGGREGATE,
          {
            pipeline: JSON.stringify(pipeline)
          }
        )

        // Return 0 if no results, otherwise return the count
        resolve(response.data.length > 0 ? response.data[0].total : 0)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const readScriptHistory = (qry, patientQry, pageSize, page) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // Build pipeline with optimal ordering
        const pipeline = []

        // 1. Apply initial filters if available (before lookups for better performance)
        if (qry) {
          pipeline.push({ $match: qry })
        }

        // 2. Skip ObjectId conversion and use direct lookup
        // This avoids issues with non-ObjectId formatted IDs like PT-xxxx

        // 3. Project out large fields early to reduce memory usage during lookups
        pipeline.push({
          $project: {
            document: 0
          }
        })

        // 4. Filter out records with non-ObjectId userRef values
        pipeline.push({
          $match: {
            $and: [
              { userRef: { $exists: true } },
              { userRef: { $ne: null } },
              {
                $expr: {
                  $and: [
                    { $eq: [{ $type: '$userRef' }, 'string'] },
                    { $eq: [{ $strLenCP: '$userRef' }, 24] },
                    {
                      $regexMatch: {
                        input: '$userRef',
                        regex: '^[0-9a-fA-F]{24}$'
                      }
                    }
                  ]
                }
              }
            ]
          }
        })

        // Perform lookups using userRef directly
        const authUsersCollection = `${process.env.REACT_APP_AGILITE_TEAM_ID}_auth_users`
        console.log('🔍 Setting up lookups with collection:', authUsersCollection)
        console.log('🔍 Team ID:', process.env.REACT_APP_AGILITE_TEAM_ID)

        // Add lookup for patient (we know userRef is valid ObjectId string now)
        const patientLookup = {
          $lookup: {
            from: authUsersCollection,
            let: { userRefId: { $toObjectId: '$userRef' } },
            pipeline: [
              {
                $match: {
                  $expr: {
                    $and: [
                      { $eq: ['$_id', '$$userRefId'] },
                      { $eq: ['$extraData.role.type', 'patient'] }
                    ]
                  }
                }
              },
              {
                $project: {
                  profileImage: 0
                }
              }
            ],
            as: 'patient'
          }
        }

        const medProfLookup = {
          $lookup: {
            from: authUsersCollection,
            let: {
              medProfRefId: {
                $cond: {
                  if: {
                    $and: [
                      { $ne: ['$medProfRef', null] },
                      { $eq: [{ $type: '$medProfRef' }, 'string'] },
                      { $eq: [{ $strLenCP: '$medProfRef' }, 24] }
                    ]
                  },
                  then: { $toObjectId: '$medProfRef' },
                  else: null
                }
              }
            },
            pipeline: [
              {
                $match: {
                  $expr: {
                    $and: [
                      { $ne: ['$$medProfRefId', null] },
                      { $eq: ['$_id', '$$medProfRefId'] },
                      { $eq: ['$extraData.role.type', 'medical_professional'] }
                    ]
                  }
                }
              },
              {
                $project: {
                  profileImage: 0
                }
              }
            ],
            as: 'medProf'
          }
        }

        pipeline.push(patientLookup, medProfLookup)

        // 5. Unwind arrays from lookups
        pipeline.push(
          { $unwind: { path: '$patient', preserveNullAndEmptyArrays: true } },
          { $unwind: { path: '$medProf', preserveNullAndEmptyArrays: true } }
        )

        // 6. Apply patient search filter if provided (must come after unwind)
        if (patientQry) {
          pipeline.push({
            $match: {
              patient: { $exists: true },
              $or: [
                {
                  $expr: {
                    $regexMatch: {
                      input: { $concat: ['$patient.firstName', ' ', '$patient.lastName'] },
                      regex: patientQry,
                      options: 'i'
                    }
                  }
                },
                { 'patient.phoneNumber': { $regex: patientQry, $options: 'i' } },
                { 'patient.email': { $regex: patientQry, $options: 'i' } },
                { 'patient.physicalAddress': { $regex: patientQry, $options: 'i' } },
                { 'patient.idNo': { $regex: patientQry, $options: 'i' } }
              ]
            }
          })
        }

        // 7. Add sorting (optional - add if needed)
        pipeline.push({
          $sort: { createdAt: -1 } // Sort by creation date, newest first
        })

        // 8. Apply pagination (always last for best performance)
        if (page && pageSize) {
          pipeline.push({ $skip: (page - 1) * pageSize }, { $limit: parseInt(pageSize) })
        }

        // Execute the aggregation
        const scriptResponse = await agilite.Connectors.execute(
          ScriptEnums.profileKeys.SCRIPTS,
          ScriptEnums.routeKeys.AGGREGATE,
          {
            pipeline: JSON.stringify(pipeline)
          }
        )

        console.log('📊 Aggregation Response:', {
          recordCount: scriptResponse.data?.length,
          firstRecord: scriptResponse.data?.[0] ? {
            id: scriptResponse.data[0]._id,
            userRef: scriptResponse.data[0].userRef,
            debugUserRef: scriptResponse.data[0]._debugUserRef,
            hasPatient: !!scriptResponse.data[0].patient,
            patientId: scriptResponse.data[0].patient?._id,
            patientName: scriptResponse.data[0].patient ?
              `${scriptResponse.data[0].patient.firstName} ${scriptResponse.data[0].patient.lastName}` :
              'No patient data'
          } : null
        })

        resolve(scriptResponse.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const readScripts = (filters, projection) => {
  return new Promise((resolve, reject) => {
    let qry = {}
    let response = null
    let errorMessage = null

    ;(async () => {
      try {
        for (const key in filters) {
          if (filters[key] !== '') {
            qry[key] = filters[key]
          }
        }

        response = await agilite.Connectors.execute(ScriptEnums.profileKeys.SCRIPTS, ScriptEnums.routeKeys.READ, {
          filter: JSON.stringify(qry),
          page: null,
          projection: projection,
          pageLimit: null
        })
        resolve(response.data)
      } catch (e) {
        if (e.response?.data?.errorMessage) {
          errorMessage = e.response.data.errorMessage
        } else if (e.message) {
          errorMessage = e.message
        } else {
          errorMessage = CoreEnums.errorMessages.UNKNOWN_ERROR
        }

        reject(errorMessage)
      }
    })()
  })
}

export const createScripts = (data) => {
  return new Promise((resolve, reject) => {
    let response = null
    let errorMessage = null
    let timestamp = Date()
    let bulkWriteData = []

    ;(async () => {
      try {
        data.createdAt = timestamp
        data.modifiedAt = timestamp
        data.createdBy = store.getState().auth.agiliteUser._id
        data.modifiedBy = store.getState().auth.agiliteUser._id
        data.medProfRef = store.getState().auth.agiliteUser._id

        if (store.getState().auth.agiliteUser.extraData.role.type !== CoreEnums.userRoles.SUPERADMIN) {
          data.entityRef = store.getState().core.entity._id
        }

        response = await agilite.Connectors.execute(ScriptEnums.profileKeys.SCRIPTS, ScriptEnums.routeKeys.CREATE, {
          data: JSON.stringify(data)
        })

        bulkWriteData = data.items.map((med) => {
          const routine = calculateRoutineFromDosage(med.scriptingDosage)

          return {
            insertOne: {
              document: {
                userRef: data.userRef,
                name: med.name,
                code: med.code,
                nappiCode: med.nappiCode,
                isChronic: med.isChronic,
                strengthMetric: med.strengthMetric1,
                doseDescription: med.scriptingDosage,
                // Custom fields
                doseForm: med.doseForm,
                route: med.route,
                dose: med.dose,
                frequency: med.frequency,
                frequencyType: med.frequencyType,
                duration: med.duration,
                durationType: med.durationType,
                // ---
                isPaused: false,
                isArchived: false,
                scriptRef: response.data._id,
                supply: {
                  isTracking: false,
                  qty: parseInt(med.dispensingVolumePacksize),
                  qtyRemaining: parseInt(med.dispensingVolumePacksize)
                },
                routine,
                history: [],
                createdAt: timestamp,
                createdBy: store.getState().auth.agiliteUser._id
              }
            }
          }
        })

        // Create medication records
        agilite.Connectors.execute(ScriptEnums.profileKeys.PATIENT_MEDICATIONS, ScriptEnums.routeKeys.BULK_WRITE, {
          data: JSON.stringify(bulkWriteData)
        })

        resolve(response.data)
      } catch (e) {
        if (e.response?.data?.errorMessage) {
          errorMessage = e.response.data.errorMessage
        } else if (e.message) {
          errorMessage = e.message
        } else {
          errorMessage = CoreEnums.errorMessages.UNKNOWN_ERROR
        }

        reject(errorMessage)
      }
    })()
  })
}

// Function to calculate routine times from scriptingDosage
export const calculateRoutineFromDosage = (scriptingDosage) => {
  if (!scriptingDosage) return []

  const routine = []
  const lowerDosage = scriptingDosage.toLowerCase()

  // Extract frequency information
  let timesPerDay = null

  // Check for hourly frequency pattern (e.g., "6 hrly", "6 hourly", "6-hourly")
  const hourlyMatch = lowerDosage.match(/(\d+)[\s-]*(hrly|hourly)/i)
  if (hourlyMatch) {
    const hours = parseInt(hourlyMatch[1], 10)

    // Calculate how many doses per day
    timesPerDay = 24 / hours

    // Starting at 6am, calculate the times throughout the day
    for (let i = 0; i < timesPerDay; i++) {
      const hour = (6 + i * hours) % 24
      routine.push(`${hour.toString().padStart(2, '0')}:00`)
    }
    return routine
  }

  // Check for daily frequency patterns
  if (/once daily|daily|od|once a day/i.test(lowerDosage)) {
    // Default to morning dose
    return ['08:00']
  }

  if (/twice daily|bd|bid|two times a day|2 times a day/i.test(lowerDosage)) {
    return ['08:00', '20:00']
  }

  if (/three times a day|tds|tid|3 times a day/i.test(lowerDosage)) {
    return ['08:00', '14:00', '20:00']
  }

  if (/four times a day|qds|qid|4 times a day/i.test(lowerDosage)) {
    return ['08:00', '12:00', '16:00', '20:00']
  }

  // Check for specific time instructions
  if (/morning/i.test(lowerDosage)) {
    routine.push('08:00')
  }

  if (/noon|midday|lunch/i.test(lowerDosage)) {
    routine.push('12:00')
  }

  if (/afternoon/i.test(lowerDosage)) {
    routine.push('16:00')
  }

  if (/evening|night|bedtime/i.test(lowerDosage)) {
    routine.push('20:00')
  }

  // If we couldn't determine a routine from the description
  if (routine.length === 0) {
    // Default to once daily in the morning
    routine.push('08:00')
  }

  return routine
}

export const updateScript = (recordId, data) => {
  return new Promise((resolve, reject) => {
    let response = null
    let errorMessage = null
    let timestamp = Date()
    ;(async () => {
      try {
        data.dateModified = timestamp
        data.modifiedBy = store.getState().auth.agiliteUser._id

        response = await agilite.Connectors.execute(ScriptEnums.profileKeys.SCRIPTS, ScriptEnums.routeKeys.UPDATE, {
          recordId,
          data: JSON.stringify(data)
        })
        resolve(response.data)
      } catch (e) {
        if (e.response?.data?.errorMessage) {
          errorMessage = e.response.data.errorMessage
        } else if (e.message) {
          errorMessage = e.message
        } else {
          errorMessage = CoreEnums.errorMessages.UNKNOWN_ERROR
        }

        reject(errorMessage)
      }
    })()
  })
}

export const removeScript = (recordId) => {
  return new Promise((resolve, reject) => {
    let response = null
    let errorMessage = null

    ;(async () => {
      try {
        response = await agilite.Connectors.execute(ScriptEnums.profileKeys.SCRIPTS, ScriptEnums.routeKeys.DELETE, {
          recordId: recordId
        })
        resolve(response.data)
      } catch (e) {
        if (e.response?.data?.errorMessage) {
          errorMessage = e.response.data.errorMessage
        } else if (e.message) {
          errorMessage = e.message
        } else {
          errorMessage = CoreEnums.errorMessages.UNKNOWN_ERROR
        }

        reject(errorMessage)
      }
    })()
  })
}

export const MedPraxProductSearch = async (searchQuery, page, pageLimit) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      let response = null

      try {
        response = await axios.post(`${process.env.REACT_APP_NODE_RED_URL}/api/medprax/medicines`, {
          body: {
            filters: [
              {
                propertyName: 'name',
                operation: 'StartsWith',
                value: searchQuery
              },
              {
                propertyName: 'Code',
                operation: 'StartsWith',
                value: searchQuery
              },
              {
                propertyName: 'NappiCode',
                operation: 'StartsWith',
                value: searchQuery
              },
              {
                propertyName: 'Name',
                operation: 'Contains',
                value: searchQuery
              }
            ],
            filterJoin: 'Or',
            ascendingOrder: true,
            sortKey: 'code',
            proj: 'name'
          },
          pageLimit: 100,
          page: 1
        })
        resolve(response.data.products.pageResult)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const MedPraxMaterialSearch = async (searchQuery, page, pageLimit, filters) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      let response = null

      try {
        response = await axios.post(`${process.env.REACT_APP_NODE_RED_URL}/api/medprax/materials`, {
          body: {
            filters: filters
              ? filters
              : [
                  {
                    propertyName: 'Name',
                    operation: 'startsWith',
                    value: searchQuery
                  },
                  {
                    propertyName: 'Code',
                    operation: 'startsWith',
                    value: searchQuery
                  },
                  {
                    propertyName: 'NappiCode',
                    operation: 'startsWith',
                    value: searchQuery
                  },
                  {
                    propertyName: 'Name',
                    operation: 'contains',
                    value: searchQuery
                  }
                ],
            filterJoin: 'Or',
            ascendingOrder: true,
            sortKey: 'code'
          },
          pageLimit: 100,
          page: 1
        })
        resolve(response.data.products.pageResult)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const MedPraxProcedureSearch = async (searchQuery, page, pageLimit, filters, filterJoin) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      let response = null
      try {
        response = await axios.post(`${process.env.REACT_APP_NODE_RED_URL}/api/medprax/procedures`, {
          body: {
            filters: filters
              ? filters
              : [
                  {
                    propertyName: 'Code',
                    operation: 'startsWith',
                    value: searchQuery
                  },
                  {
                    propertyName: 'Description',
                    operation: 'startsWith',
                    value: searchQuery
                  },
                  {
                    propertyName: 'Description',
                    operation: 'contains',
                    value: searchQuery
                  }
                ],
            filterJoin: filterJoin ? filterJoin : 'Or',
            ascendingOrder: true,
            sortKey: 'code'
          },
          pageLimit: 100,
          page: 1
        })
        resolve(response.data.tariffCodes.pageResult)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const MedPraxICD10Search = async (searchQuery, page, pageLimit) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      let response = null

      try {
        response = await axios.post(`${process.env.REACT_APP_NODE_RED_URL}/api/medprax/icd10/search`, {
          body: {
            filters: [
              {
                propertyName: 'description',
                operation: 'StartsWith',
                value: searchQuery
              },
              {
                propertyName: 'code',
                operation: 'StartsWith',
                value: searchQuery
              },
              {
                propertyName: 'description',
                operation: 'Contains',
                value: searchQuery
              }
            ],
            filterJoin: 'Or',
            ascendingOrder: true,
            sortKey: 'code'
          },
          pageLimit: 100,
          page: 1
        })
        resolve(response.data.icd10s.pageResult)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const MedPraxSchemeSearch = async (searchQuery, page, pageLimit) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      let response = null

      try {
        response = await axios.post(`${process.env.REACT_APP_NODE_RED_URL}/api/medprax/schemes`, {
          body: {
            filters: [
              {
                propertyName: 'name',
                operation: 'Contains',
                value: searchQuery ? searchQuery : ''
              }
            ],
            filterJoin: 'And',
            ascendingOrder: true,
            sortKey: 'Code'
          },
          pageLimit: 1000,
          page: 1
        })
        resolve(response.data.schemes.pageResult)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const MedPraxSchemePlanSearch = async (searchQuery, page, pageLimit) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      let response = null

      try {
        response = await axios.post(`${process.env.REACT_APP_NODE_RED_URL}/api/medprax/scheme_plans`, {
          body: {
            filters: [
              {
                propertyName: 'schemeCode',
                operation: 'Equals',
                value: searchQuery
              }
            ],
            filterJoin: 'And',
            ascendingOrder: true,
            sortKey: 'Code'
          },
          pageLimit: 100,
          page: 1
        })
        resolve(response.data.plans.pageResult)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const MedPraxSchemePlanOptionSearch = async (searchQuery, page, pageLimit) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      let response = null

      try {
        response = await axios.post(`${process.env.REACT_APP_NODE_RED_URL}/api/medprax/scheme_plan_options`, {
          body: {
            filters: [
              {
                propertyName: 'planCode',
                operation: 'Equals',
                value: searchQuery
              }
            ],
            filterJoin: 'And',
            ascendingOrder: true,
            sortKey: 'Code'
          },
          pageLimit: 100,
          page: 1
        })
        resolve(response.data.planOptions.pageResult)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const readThread = async (thread_id) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      let assistant_response = null

      try {
        assistant_response = await axios.post(
          `${process.env.REACT_APP_NODE_RED_URL}/api/assistants/drug-interactions/response`,
          {
            thread_id
          }
        )
        resolve(assistant_response)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const AssistantDrugInteractionCheck = async (script) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      let response = null

      try {
        response = await axios.post(`${process.env.REACT_APP_NODE_RED_URL}/api/assistants/drug-interactions/query`, {
          drugs: script.map((i) => i.name).join(', ')
        })

        if (response.data.thread_id) {
          resolve(response.data.thread_id)
        } else {
          throw new Error('There was an issue contacting your assistant.')
        }
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const readScriptTemplates = (filters) => {
  return new Promise((resolve, reject) => {
    let qry = { medProfRef: store.getState().auth.agiliteUser._id }
    let response = null
    let errorMessage = null

    ;(async () => {
      try {
        for (const key in filters) {
          if (filters[key] !== '') {
            qry[key] = filters[key]
          }
        }

        response = await agilite.Connectors.execute(
          ScriptEnums.profileKeys.SCRIPT_TEMPLATES,
          ScriptEnums.routeKeys.READ,
          {
            filter: JSON.stringify(qry),
            page: null,
            pageLimit: null
          }
        )
        resolve(response.data)
      } catch (e) {
        if (e.response?.data?.errorMessage) {
          errorMessage = e.response.data.errorMessage
        } else if (e.message) {
          errorMessage = e.message
        } else {
          errorMessage = CoreEnums.errorMessages.UNKNOWN_ERROR
        }

        reject(errorMessage)
      }
    })()
  })
}

export const createScriptTemplateEntry = (data) => {
  return new Promise((resolve, reject) => {
    let response = null
    let errorMessage = null
    let timestamp = Date()

    ;(async () => {
      try {
        data.createdAt = timestamp
        data.modifiedAt = timestamp
        data.createdBy = store.getState().auth.agiliteUser._id
        data.modifiedBy = store.getState().auth.agiliteUser._id
        data.medProfRef = store.getState().auth.agiliteUser._id
        response = await agilite.Connectors.execute(
          ScriptEnums.profileKeys.SCRIPT_TEMPLATES,
          ScriptEnums.routeKeys.CREATE,
          {
            data: JSON.stringify(data)
          }
        )
        resolve(response.data)
      } catch (e) {
        if (e.response?.data?.errorMessage) {
          errorMessage = e.response.data.errorMessage
        } else if (e.message) {
          errorMessage = e.message
        } else {
          errorMessage = CoreEnums.errorMessages.UNKNOWN_ERROR
        }

        reject(errorMessage)
      }
    })()
  })
}

export const updateScriptTemplate = (recordId, data) => {
  return new Promise((resolve, reject) => {
    let response = null
    let errorMessage = null
    let timestamp = Date()
    ;(async () => {
      try {
        data.dateModified = timestamp
        data.modifiedBy = store.getState().auth.agiliteUser._id

        response = await agilite.Connectors.execute(
          ScriptEnums.profileKeys.SCRIPT_TEMPLATES,
          ScriptEnums.routeKeys.UPDATE,
          {
            recordId,
            data: JSON.stringify(data)
          }
        )
        resolve(response.data)
      } catch (e) {
        if (e.response?.data?.errorMessage) {
          errorMessage = e.response.data.errorMessage
        } else if (e.message) {
          errorMessage = e.message
        } else {
          errorMessage = CoreEnums.errorMessages.UNKNOWN_ERROR
        }

        reject(errorMessage)
      }
    })()
  })
}

export const removeScriptTemplate = (recordId) => {
  return new Promise((resolve, reject) => {
    let response = null
    let errorMessage = null

    ;(async () => {
      try {
        response = await agilite.Connectors.execute(
          ScriptEnums.profileKeys.SCRIPT_TEMPLATES,
          ScriptEnums.routeKeys.DELETE,
          {
            recordId: recordId
          }
        )
        resolve(response.data)
      } catch (e) {
        if (e.response?.data?.errorMessage) {
          errorMessage = e.response.data.errorMessage
        } else if (e.message) {
          errorMessage = e.message
        } else {
          errorMessage = CoreEnums.errorMessages.UNKNOWN_ERROR
        }

        reject(errorMessage)
      }
    })()
  })
}

export const ProductSearchAgilite = async (searchQuery, type, preload) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      const qry = {
        $or: [
          { 'data.name': { $regex: searchQuery, $options: 'i' } },
          { 'data.code': { $regex: searchQuery, $options: 'i' } },
          { 'data.nappiCode': { $regex: searchQuery, $options: 'i' } }
        ]
      }
      let response = null

      try {
        if (preload) {
          response = await agilite.Connectors.execute('medprax_products', 'find', {
            filter: JSON.stringify({ type }),
            projection: 'data.code data.name data.strengthMetric1 data.unitOfMeasure1 data.standardPacksize'
            // pageLimit: 18000
          })
        } else {
          if (type) {
            qry['type'] = type
          }

          response = await agilite.Connectors.execute('medprax_products', 'find', {
            filter: JSON.stringify(qry)
            // projection:
            //   'type data.code data.name data.strengthMetric1 data.unitOfMeasure1 data.presentationCode data.scriptingDosage data.standardPacksize data.drugSchedule data.nappiCode'
          })
        }

        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const getDischems = () => {
  return new Promise((resolve, reject) => {
    let response = null
    let errorMessage = null

    ;(async () => {
      try {
        response = await agilite.Connectors.execute(ScriptEnums.profileKeys.DISCHEMS, ScriptEnums.routeKeys.READ)
        resolve(response.data)
      } catch (e) {
        if (e.response?.data?.errorMessage) {
          errorMessage = e.response.data.errorMessage
        } else if (e.message) {
          errorMessage = e.message
        } else {
          errorMessage = CoreEnums.errorMessages.UNKNOWN_ERROR
        }

        reject(errorMessage)
      }
    })()
  })
}

export const sendDischemScript = (data) => {
  return new Promise((resolve, reject) => {
    let response = null
    let errorMessage = null

    ;(async () => {
      try {
        response = await axios.post(`${process.env.REACT_APP_NODE_RED_URL}/api/dischem/new-order`, data, {
          headers: {
            'api-key': process.env.REACT_APP_AGILITE_API_KEY
          }
        })
        resolve(response.data)
      } catch (e) {
        if (e.response?.data?.errorMessage) {
          errorMessage = e.response.data.errorMessage
        } else if (e.message) {
          errorMessage = e.message
        } else {
          errorMessage = CoreEnums.errorMessages.UNKNOWN_ERROR
        }

        reject(errorMessage)
      }
    })()
  })
}
